import { localMondayISO } from '../lib/dates';
import { deadCount, pendingCount } from '../lib/queue';
import { adminActive, goToCurrentWeek } from '../state/admin';
import { showHubSheet, showNewWeek, showReminders } from '../state/adminUi';
import { dueCount } from '../state/reminders';
import { lastUpdated, pickWeek, stepWeek, week, weeks } from '../state/schedule';

function relTime(ms: number): string {
  if (!ms) return '';
  const m = Math.floor((Date.now() - ms) / 60000);
  return m < 1 ? 'Updated just now' : `Updated ${m}m ago`;
}

/** Dark brand bar: wordmark, week picker (native select for a11y), status line with
 *  the §7 sync state: online/offline, N pending, N failed (opens the attention sheet). */
export function Header({
  onRefresh,
  onAttention,
}: {
  onRefresh: () => void;
  onAttention: () => void;
}) {
  const monday = localMondayISO();
  const admin = adminActive.value;
  // The picker holds current + future only (archived weeks live in the hub archive);
  // while VIEWING an archived week it appears as a temporary labeled entry.
  const list = weeks.value
    .filter((w) => w.weekId >= monday)
    .sort((a, b) => b.weekId.localeCompare(a.weekId));
  const cur = week.value?.weekId ?? '';
  const viewingArchived = !!cur && !list.some((w) => w.weekId === cur);
  const hasCurrentWeek = weeks.value.some((w) => w.weekId === monday);

  function onPick(v: string): void {
    if (v === '__new__') {
      showNewWeek.value = true;
      return;
    }
    if (v === '__current__') {
      void goToCurrentWeek(monday, weeks.value.find((w) => w.weekId === monday)?.weekId);
      return;
    }
    pickWeek(v);
  }

  return (
    <header class="sticky top-0 z-30 bg-band pt-[env(safe-area-inset-top)] text-white shadow-lg">
      <div class="mx-auto flex max-w-3xl items-center gap-2 px-3 py-2">
        <div class="text-lg font-extrabold">
          <span class="text-brand-500">Δ</span>T Schedule
        </div>
        <div class="ml-auto flex items-center gap-1.5">
          {/* Week arrows: desktop/tablet only (v1 v102 kept phones to the dropdown). */}
          <button
            class="tap wk-nav hidden h-11 w-11 rounded-lg bg-white/10 text-lg sm:block"
            aria-label="Previous week"
            onClick={() => stepWeek(-1)}
          >
            ‹
          </button>
          <select
            class="tap h-11 max-w-[200px] rounded-lg border-0 bg-white/10 px-2 text-sm font-semibold text-white"
            aria-label="Week"
            value={cur}
            onChange={(e) => {
              const el = e.target as HTMLSelectElement;
              const v = el.value;
              el.value = cur; // action rows aren't a selection; a real pick re-renders
              onPick(v);
            }}
          >
            {viewingArchived && (
              <option value={cur} class="text-navy-900">
                Archived · {week.value?.weekOf ?? cur}
              </option>
            )}
            {list.map((w) => (
              <option key={w.weekId} value={w.weekId} class="text-navy-900">
                {w.weekOf} · {w.count}
              </option>
            ))}
            {admin && !hasCurrentWeek && (
              <option value="__current__" class="text-navy-900">
                Go to current week
              </option>
            )}
            {admin && (
              <option value="__new__" class="text-navy-900">
                ＋ New week…
              </option>
            )}
          </select>
          <button
            class="tap wk-nav hidden h-11 w-11 rounded-lg bg-white/10 text-lg sm:block"
            aria-label="Next week"
            onClick={() => stepWeek(1)}
          >
            ›
          </button>
          {admin && (
            <button
              class="tap relative h-11 w-11 rounded-lg bg-white/10"
              aria-label="Reminders"
              data-testid="btn-bell"
              onClick={() => (showReminders.value = true)}
            >
              <svg
                viewBox="0 0 24 24"
                class="mx-auto h-5 w-5"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M12 22a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4a1.5 1.5 0 0 0-3 0v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
              </svg>
              {dueCount.value > 0 && (
                <span
                  class="absolute -top-1 -right-1 min-w-4.5 rounded-full bg-red-600 px-1 text-[11px] leading-[18px] font-bold"
                  data-testid="bell-badge"
                >
                  {dueCount.value}
                </span>
              )}
            </button>
          )}
          {admin && (
            <button
              class="tap h-11 rounded-lg bg-white/10 px-3 text-sm font-bold lg:hidden"
              data-testid="btn-hub"
              onClick={() => (showHubSheet.value = true)}
            >
              Hub
            </button>
          )}
          <button
            class="tap h-11 w-11 rounded-lg bg-white/10"
            aria-label="Refresh"
            onClick={onRefresh}
          >
            <svg
              viewBox="0 0 24 24"
              class="mx-auto h-5 w-5"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
            >
              <path
                d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
      <div class="mx-auto flex max-w-3xl items-center gap-2 px-3 pb-1.5 text-xs text-white/70">
        <span
          class={`inline-block h-2 w-2 rounded-full ${navigator.onLine ? 'bg-emerald-400' : 'bg-red-400'}`}
          aria-hidden="true"
        />
        <span data-testid="status-line">
          {navigator.onLine ? relTime(lastUpdated.value) : 'Offline — changes saved on this phone'}
        </span>
        {pendingCount.value > 0 && (
          <span
            class="rounded-full bg-brand-600 px-2 py-0.5 font-semibold text-white"
            data-testid="pending-badge"
          >
            {pendingCount.value} to sync
          </span>
        )}
        {deadCount.value > 0 && (
          <button
            class="tap !min-h-0 rounded-full bg-red-600 px-2 py-0.5 font-semibold text-white"
            data-testid="attention-badge"
            onClick={onAttention}
          >
            {deadCount.value} need attention
          </button>
        )}
      </div>
    </header>
  );
}
